import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, PencilSimple, Trash, FloppyDisk, X, GraduationCap, UsersThree } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { logError } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: '', company: 'Tulsi Jobs', employee_code: '', designation: '', basic_salary: '', travelling_allowance: '', joining_date: '', on_training: false };
const COMPANIES = ['Tulsi Jobs', 'National Institute of Learning', 'Tulsi Makeup & Fitness Studio'];
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const inputCls = "bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2";

export const StaffTab = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/staff`);
      setStaff(data);
    } catch (e) {
      logError('staff-list', e);
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => { setForm(emptyForm); setAddOpen(true); };
  const openEdit = (s) => {
    setEditTarget(s);
    setForm({ ...emptyForm, ...s, basic_salary: String(s.basic_salary ?? ''), travelling_allowance: String(s.travelling_allowance ?? '') });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Staff name is required'); return; }
    const payload = {
      ...form,
      basic_salary: parseFloat(form.basic_salary) || 0,
      travelling_allowance: parseFloat(form.travelling_allowance) || 0,
    };
    try {
      if (editTarget) {
        await axios.patch(`${API}/staff/${editTarget.id}`, payload);
        toast.success(`Updated ${form.name}`);
        setEditTarget(null);
      } else {
        await axios.post(`${API}/staff`, payload);
        toast.success(`Added ${form.name}`);
        setAddOpen(false);
      }
      fetchAll();
    } catch (err) {
      logError('staff-save', err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete ${s.name}? Past salary records are kept.`)) return;
    try {
      await axios.delete(`${API}/staff/${s.id}`);
      toast.success(`Deleted ${s.name}`);
      fetchAll();
    } catch (err) {
      logError('staff-delete', err);
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleTraining = async (s) => {
    try {
      await axios.patch(`${API}/staff/${s.id}`, { on_training: !s.on_training });
      toast.success(!s.on_training ? `${s.name} marked ON TRAINING (only travelling allowance)` : `${s.name} training completed — full salary starts`);
      fetchAll();
    } catch (err) {
      logError('staff-training', err);
      toast.error('Failed to update training status');
    }
  };

  return (
    <div data-testid="staff-tab">
      <div className="flex justify-end mb-4">
        <Button
          data-testid="add-staff-button"
          onClick={openAdd}
          className="bg-[#A3E635] text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all"
        >
          <Plus size={20} weight="bold" className="mr-2" />
          Add Staff
        </Button>
      </div>

      {loading ? (
        <div className="text-white font-bold">Loading…</div>
      ) : (
        <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] overflow-x-auto">
          <table className="w-full tracker-table" data-testid="staff-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Emp ID</th>
                <th>Designation</th>
                <th>Basic Salary</th>
                <th>Travelling</th>
                <th>Joining</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-6 text-white">No staff yet. Click <strong>Add Staff</strong> to add your first employee.</td></tr>
              ) : staff.map((s) => (
                <tr key={s.id} data-testid={`staff-row-${s.id}`}>
                  <td className="font-bold flex items-center gap-2">
                    <UsersThree size={18} weight="duotone" className="text-[#A3E635]" />
                    {s.name}
                  </td>
                  <td className="text-xs font-bold text-[#FDE047]">{s.company || 'Tulsi Jobs'}</td>
                  <td className="text-xs">{s.employee_code || <span className="text-[#888]">—</span>}</td>
                  <td className="text-xs">{s.designation || <span className="text-[#888]">—</span>}</td>
                  <td className="font-bold">{inr(s.basic_salary)}</td>
                  <td>{inr(s.travelling_allowance)}</td>
                  <td className="text-xs">{s.joining_date || <span className="text-[#888]">—</span>}</td>
                  <td>
                    <button
                      data-testid={`training-toggle-${s.id}`}
                      onClick={() => toggleTraining(s)}
                      title={s.on_training ? 'On training — click to mark training complete' : 'Click to put on training (only travelling allowance)'}
                      className={`px-2.5 py-1.5 border-2 border-black rounded text-xs font-bold flex items-center gap-1 transition-all hover:-translate-y-0.5 ${
                        s.on_training ? 'bg-[#FDE047] text-black' : 'bg-[#A3E635] text-black'
                      }`}
                    >
                      <GraduationCap size={14} weight="bold" />
                      {s.on_training ? 'TRAINING' : 'ACTIVE'}
                    </button>
                  </td>
                  <td>
                    <div className="flex gap-1.5">
                      <button
                        data-testid={`edit-staff-${s.id}`}
                        onClick={() => openEdit(s)}
                        title="Edit"
                        className="p-2 bg-[#93C5FD] border-2 border-black rounded hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
                      >
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button
                        data-testid={`delete-staff-${s.id}`}
                        onClick={() => handleDelete(s)}
                        title="Delete"
                        className="p-2 bg-[#F87171] text-white border-2 border-black rounded hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] transition-all"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={addOpen || !!editTarget} onOpenChange={(v) => { if (!v) { setAddOpen(false); setEditTarget(null); } }}>
        <DialogContent className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
              <UsersThree size={26} weight="duotone" />
              {editTarget ? 'Edit Staff' : 'Add Staff'}
            </DialogTitle>
            <DialogDescription className="sr-only">Manage staff details for salary calculation</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Name *</Label>
                <Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className={inputCls} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Employee ID</Label>
                <Input data-testid="staff-code-input" value={form.employee_code} onChange={(e) => setForm({...form, employee_code: e.target.value})} placeholder="TJ-001" className={inputCls} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Company *</Label>
              <select
                data-testid="staff-company-select"
                value={form.company}
                onChange={(e) => setForm({...form, company: e.target.value})}
                className="w-full bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md px-3 py-2 text-sm font-bold"
              >
                {COMPANIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Designation</Label>
                <Input data-testid="staff-designation-input" value={form.designation} onChange={(e) => setForm({...form, designation: e.target.value})} placeholder="Recruiter" className={inputCls} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Joining Date</Label>
                <Input data-testid="staff-joining-input" type="date" value={form.joining_date} onChange={(e) => setForm({...form, joining_date: e.target.value})} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Basic Salary (₹/month)</Label>
                <Input data-testid="staff-basic-input" type="number" min="0" step="any" value={form.basic_salary} onChange={(e) => setForm({...form, basic_salary: e.target.value})} placeholder="12000" className={inputCls} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">Travelling Allowance (₹)</Label>
                <Input data-testid="staff-ta-input" type="number" min="0" step="any" value={form.travelling_allowance} onChange={(e) => setForm({...form, travelling_allowance: e.target.value})} placeholder="1500" className={inputCls} />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer bg-[#FEF9C3] border-2 border-black rounded-md px-3 py-2.5">
              <input
                data-testid="staff-training-checkbox"
                type="checkbox"
                checked={form.on_training}
                onChange={(e) => setForm({...form, on_training: e.target.checked})}
                className="w-4 h-4 accent-black"
              />
              <span className="text-sm font-bold text-[#0A0A0A] flex items-center gap-1.5">
                <GraduationCap size={16} weight="bold" />
                On Training Period — only travelling allowance is paid until training completes
              </span>
            </label>
            <div className="flex gap-2 pt-2">
              <Button type="submit" data-testid="submit-staff" className="flex-1 bg-[#A3E635] text-black border-2 border-black rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all">
                <FloppyDisk size={18} weight="bold" className="mr-2" />
                {editTarget ? 'Save Changes' : 'Add Staff'}
              </Button>
              <Button type="button" onClick={() => { setAddOpen(false); setEditTarget(null); }} className="bg-[#404040] text-white border-2 border-black rounded-md font-bold px-4 py-2">
                <X size={18} weight="bold" />
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffTab;
